import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auditService } from '../../services/auditService';
import { normaliseHeader } from '../../reports/engine';
import { readPreview } from '../../components/MasterFileUploader';
import DataGrid, { actionsColumn, dash, numberColumn } from '../../components/DataGrid';
import { downloadFile, fmtQty, safeFileName } from '../Reports/ui';
import { PageLoader } from '../../components/Loader';

const EFFECT_LABEL = {
  add: 'Adds to counted qty',
  subtract: 'Subtracts from counted qty',
  none: 'Shown only (no effect on variance)',
};
const EFFECT_SHORT = { add: '+ adds', subtract: '− subtracts', none: 'note only' };

const FIELDS = [
  { key: 'ean', label: 'EAN / article', aliases: ['ean', 'barcode', 'bar code', 'ean code', 'ean no', 'upc', 'gtin', 'article', 'article no', 'item code', 'sku'] },
  { key: 'quantity', label: 'Quantity', aliases: ['qty', 'quantity', 'wbc qty', 'wbc', 'count', 'stock', 'pcs', 'units', 'damaged qty', 'loose qty', 'pending qty', 'bill qty'] },
  { key: 'location', label: 'Location', aliases: ['location', 'loc', 'rack', 'shelf', 'bin', 'zone'] },
  { key: 'remark', label: 'Remark', aliases: ['remark', 'remarks', 'comment', 'comments', 'note', 'notes', 'reason', 'bill no', 'bill number'] },
];

// Example rows for the downloadable CSV template of each standard tab.
const TEMPLATE_EXAMPLES = {
  wbc: [['8901262010016', '12', 'Back store', 'WBC sheet from client']],
  damaged: [['8901262010016', '2', 'Damage bin', 'Torn packs']],
  staples: [['8901262010023', '5', 'Staples rack', 'Loose rice (kg)']],
  pending_bills: [['8901262010016', '1', '', 'Bill 4512 - sold, not billed yet']],
};
const REMARK_EXAMPLE = [
  ['', '', 'Rack 4', 'Rack locked, key with store manager'],
  ['8901262010016', '', '', 'Price label missing'],
];

function downloadTemplate(category) {
  const csvCell = (value) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
  const examples = category.is_custom ? REMARK_EXAMPLE : TEMPLATE_EXAMPLES[category.code] || TEMPLATE_EXAMPLES.wbc;
  const lines = [['EAN', 'Qty', 'Location', 'Remark'], ...examples].map((row) => row.map(csvCell).join(','));
  downloadFile(`${safeFileName(category.name)} template.csv`, `\ufeff${lines.join('\r\n')}\r\n`, 'text/csv;charset=utf-8');
}

const clean = (value) => String(value ?? '').replace(/[\t\r\n]+/g, ' ').trim();

function when(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

const PREVIEW_ROWS = 15;
const looksLikeEan = (value) => /^\d{8,14}$/.test(clean(value));
const looksLikeQty = (value) => /^-?\d{1,7}([.,]\d+)?$/.test(clean(value).replace(/,(?=\d{3}\b)/g, ''));

/** Header row = first of the first rows that names an EAN or quantity column. */
function findHeader(rows) {
  const names = new Set([...FIELDS[0].aliases, ...FIELDS[1].aliases]);
  for (let index = 0; index < Math.min(rows.length, PREVIEW_ROWS); index += 1) {
    if (rows[index].some((cell) => names.has(normaliseHeader(cell)))) return index;
  }
  return -1;
}

/** Aliases are in order of preference: the first one found wins. */
function guessByHeader(header) {
  const names = header.map((cell) => normaliseHeader(cell));
  const mapping = {};
  FIELDS.forEach((field) => {
    let index = -1;
    for (const alias of field.aliases) {
      index = names.indexOf(alias);
      if (index >= 0) break;
    }
    mapping[field.key] = index >= 0 ? String(index) : '';
  });
  return mapping;
}

/**
 * No header: EAN = column with the most 8-14 digit values; quantity = another
 * column of small numbers; remark = the column with the most text.
 */
function guessByContent(rows) {
  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const score = Array.from({ length: width }, (_, col) => ({
    col,
    ean: rows.filter((row) => looksLikeEan(row[col])).length,
    qty: rows.filter((row) => looksLikeQty(row[col]) && !looksLikeEan(row[col])).length,
    text: rows.reduce((sum, row) => sum + (/[a-z]/i.test(row[col] || '') ? String(row[col]).length : 0), 0),
  }));
  const ean = [...score].sort((a, b) => b.ean - a.ean)[0];
  const qty = score.filter((s) => s.col !== ean?.col).sort((a, b) => b.qty - a.qty)[0];
  const remark = score.filter((s) => s.col !== ean?.col && s.col !== qty?.col).sort((a, b) => b.text - a.text)[0];
  return {
    ean: ean && ean.ean ? String(ean.col) : '',
    quantity: qty && qty.qty ? String(qty.col) : '',
    location: '',
    remark: remark && remark.text ? String(remark.col) : '',
  };
}

/**
 * Upload of a client sheet (.txt / .csv / .xlsx / .xlsb) into one
 * adjustment tab - the same steps as the master file upload: pick the sheet,
 * the header row and which columns hold the EAN, quantity, location and
 * remark (guessed from the header names, or from the values when there is
 * no header row).
 */
function AdjustmentUploader({ tabName, remarksOnly, onUpload, onCancel }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [source, setSource] = useState(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(-1); // -1 = no header row
  const [mapping, setMapping] = useState({ ean: '', quantity: '', location: '', remark: '' });
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const sheet = source?.sheets[sheetIndex];
  const previewRows = useMemo(() => sheet?.rows || [], [sheet]);

  const applyGuess = (rows) => {
    const header = findHeader(rows);
    setHeaderRow(header);
    setMapping(header >= 0 ? guessByHeader(rows[header]) : guessByContent(rows));
  };

  const choose = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;
    setError('');
    setReading(true);
    try {
      const read = await readPreview(picked);
      const first = Math.max(0, read.sheets.findIndex((s) => s.rows.length > 0));
      if (!read.sheets[first]?.rows.length) throw new Error('The file has no rows.');
      setFile(picked);
      setSource(read);
      setSheetIndex(first);
      applyGuess(read.sheets[first].rows);
    } catch (err) {
      setError(err.message);
      setFile(null);
      setSource(null);
    } finally {
      setReading(false);
    }
  };

  const columns = useMemo(() => {
    const width = previewRows.reduce((max, row) => Math.max(max, row.length), 0);
    const header = headerRow >= 0 ? previewRows[headerRow] || [] : [];
    return Array.from({ length: width }, (_, index) => {
      const sample = previewRows.find((row, rowIndex) => rowIndex > headerRow && clean(row[index]));
      return {
        index,
        label: clean(header[index]) || `Column ${index + 1}`,
        sample: sample ? clean(sample[index]).slice(0, 24) : '',
      };
    });
  }, [previewRows, headerRow]);

  const pick = useCallback(
    (row, key) => (mapping[key] === '' ? '' : clean(row[Number(mapping[key])])),
    [mapping],
  );

  const preview = useMemo(
    () =>
      previewRows
        .slice(headerRow + 1)
        .filter((row) => row.some((cell) => clean(cell)))
        .slice(0, 5)
        .map((row) => ({
          ean: pick(row, 'ean'),
          quantity: pick(row, 'quantity'),
          location: pick(row, 'location'),
          remark: pick(row, 'remark'),
        })),
    [previewRows, headerRow, pick],
  );

  const required = remarksOnly ? [] : ['ean', 'quantity'];
  const ready = remarksOnly ? mapping.ean !== '' || mapping.remark !== '' : mapping.ean !== '' && mapping.quantity !== '';

  const upload = async (replace) => {
    setBusy(true);
    setError('');
    try {
      const all = await source.readAll(sheetIndex);
      const rows = [];
      all.slice(headerRow + 1).forEach((row, index) => {
        if (!row.some((cell) => clean(cell))) return;
        rows.push({
          line: headerRow + index + 2,
          ean: pick(row, 'ean'),
          quantity: pick(row, 'quantity').replace(/,/g, ''),
          location: pick(row, 'location'),
          remark: pick(row, 'remark'),
        });
      });
      if (!rows.length) throw new Error('No rows below the header row.');
      await onUpload(rows, replace, file.name);
    } catch (err) {
      setError(`Not uploaded: ${err.message}`);
      setBusy(false);
    }
  };

  const working = reading || busy;

  return (
    <div className="master-upload adjust-upload">
      <div className="master-upload__row">
        <button type="button" className="master-upload__choose" onClick={() => inputRef.current?.click()} disabled={working}>
          {reading ? 'Reading file…' : file ? `File: ${file.name}` : `Choose file for ${tabName} (.txt, .csv, .xlsx, .xlsb)…`}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".txt,.tsv,.csv,.xlsx,.xlsb"
          onChange={choose}
          hidden
          aria-label="Adjustment file"
        />
        {!working ? (
          <button type="button" className="master-upload__link" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>

      {error ? <p className="master-upload__error">{error}</p> : null}

      {source && sheet ? (
        <>
          <div className="master-upload__grid">
            {source.sheets.length > 1 ? (
              <label>
                Sheet
                <select
                  value={sheetIndex}
                  onChange={(event) => {
                    const index = Number(event.target.value);
                    setSheetIndex(index);
                    applyGuess(source.sheets[index].rows);
                  }}
                  disabled={working}
                >
                  {source.sheets.map((s, index) => (
                    <option key={s.name} value={index}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              Header row
              <select
                value={headerRow}
                onChange={(event) => {
                  const row = Number(event.target.value);
                  setHeaderRow(row);
                  setMapping(row >= 0 ? guessByHeader(previewRows[row] || []) : guessByContent(previewRows));
                }}
                disabled={working}
              >
                <option value={-1}>No header row (data starts at row 1)</option>
                {previewRows.map((row, index) => (
                  <option key={index} value={index}>
                    Row {index + 1}: {row.slice(0, 4).map((c) => clean(c) || '—').join(' | ')}
                  </option>
                ))}
              </select>
            </label>
            {FIELDS.map((field) => {
              const isRequired = required.includes(field.key);
              return (
                <label key={field.key}>
                  {field.label}
                  {isRequired ? ' *' : ''}
                  <select
                    aria-label={`${field.label} column`}
                    value={mapping[field.key] ?? ''}
                    onChange={(event) => setMapping((prev) => ({ ...prev, [field.key]: event.target.value }))}
                    disabled={working}
                  >
                    <option value="">{isRequired ? 'Choose column…' : 'Not in this file'}</option>
                    {columns.map((column) => (
                      <option key={column.index} value={String(column.index)}>
                        {column.index + 1}: {column.label}
                        {column.sample ? ` (e.g. ${column.sample})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              );
            })}
          </div>

          {!ready ? (
            <p className="master-upload__hint">
              {remarksOnly ? 'Choose the EAN or the remark column.' : 'Choose the columns that hold the EAN and the quantity.'}
            </p>
          ) : (
            <div className="master-upload__preview">
              <table>
                <thead>
                  <tr>
                    <th>EAN</th>
                    <th>Qty</th>
                    <th>Location</th>
                    <th>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, index) => (
                    <tr key={index}>
                      <td className={!row.ean || looksLikeEan(row.ean) ? '' : 'master-upload__warn'}>{row.ean || '—'}</td>
                      <td className={!row.quantity || looksLikeQty(row.quantity) ? '' : 'master-upload__warn'}>
                        {row.quantity || '—'}
                      </td>
                      <td>{row.location || '—'}</td>
                      <td>{row.remark || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="master-upload__row">
            <button type="button" className="master-upload__submit" disabled={!ready || working} onClick={() => upload(false)}>
              {busy ? 'Uploading…' : 'Add to this tab'}
            </button>
            <button
              type="button"
              className="compare-button compare-button--ghost"
              disabled={!ready || working}
              onClick={() => upload(true)}
            >
              Replace this tab
            </button>
            <span className="master-upload__hint">
              EANs in orange don't look like barcodes - article numbers are fine, they are matched to the master.
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** One row typed in by hand. */
function AddRowForm({ remarksOnly, onAdd }) {
  const empty = { ean: '', quantity: '', location: '', remark: '' };
  const [row, setRow] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (key) => (event) => setRow((prev) => ({ ...prev, [key]: event.target.value }));
  const ready = remarksOnly ? row.ean.trim() || row.remark.trim() : row.ean.trim() && row.quantity !== '';

  const submit = async (event) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    try {
      await onAdd({ ...row, ean: row.ean.trim() });
      setRow(empty);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="adjust-add" onSubmit={submit}>
      <input value={row.ean} onChange={set('ean')} placeholder={remarksOnly ? 'EAN (optional)' : 'EAN or article'} aria-label="EAN" />
      <input
        value={row.quantity}
        onChange={set('quantity')}
        placeholder={remarksOnly ? 'Qty (optional)' : 'Qty'}
        inputMode="decimal"
        aria-label="Quantity"
      />
      <input value={row.location} onChange={set('location')} placeholder="Location (optional)" aria-label="Location" />
      <input
        value={row.remark}
        onChange={set('remark')}
        placeholder={remarksOnly ? 'Remark' : 'Remark (optional)'}
        aria-label="Remark"
        className="adjust-add__remark"
      />
      <button type="submit" className="compare-button" disabled={!ready || busy}>
        {busy ? 'Adding…' : 'Add row'}
      </button>
    </form>
  );
}

/**
 * "Manual adjustments" tab of the schedule portal: stock counted outside the
 * scans - WBC data from the client, damaged & defective stock, staples loose
 * stock, pending bills - and free remark tabs. Each tab says whether its
 * quantities add to, subtract from or don't change the counted quantity in
 * the variance report. Admin and Data ASA edit; everyone else views.
 */
function AdjustmentsPanel({ auditId, canEdit, refreshKey }) {
  const [categories, setCategories] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const list = await auditService.getAdjustments(auditId);
      setCategories(list);
      setActiveId((current) => (list.some((c) => c.id === current) ? current : list[0]?.id ?? null));
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [auditId]);

  const loadRows = useCallback(async () => {
    if (!activeId) return;
    try {
      setRows(await auditService.getAdjustmentRows(auditId, activeId));
    } catch (err) {
      setError(err.message);
    }
  }, [auditId, activeId]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories, refreshKey]);

  useEffect(() => {
    setRows([]);
    loadRows();
  }, [loadRows, refreshKey]);

  const active = categories?.find((c) => c.id === activeId) || null;

  const run = async (action, message) => {
    setError('');
    setNotice('');
    try {
      const result = await action();
      if (message) setNotice(typeof message === 'function' ? message(result) : message);
      await Promise.all([loadCategories(), loadRows()]);
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const addRemarksTab = () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('Name of the new remarks tab', 'Remarks');
    if (!name || !name.trim()) return;
    run(async () => {
      const created = await auditService.addAdjustmentCategory(auditId, { name: name.trim() });
      setActiveId(created.id);
      return created;
    }, `Added the tab "${name.trim()}".`);
  };

  const renameTab = () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('New name for this tab', active.name);
    if (!name || !name.trim() || name.trim() === active.name) return;
    run(() => auditService.updateAdjustmentCategory(auditId, active.id, { name: name.trim() }), 'Tab renamed.');
  };

  const deleteTab = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the tab "${active.name}" and its ${active.rows} row(s)?`)) return;
    run(() => auditService.deleteAdjustmentCategory(auditId, active.id), 'Tab deleted.');
  };

  const clearTab = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove all ${active.rows} row(s) from "${active.name}"?`)) return;
    run(() => auditService.clearAdjustmentRows(auditId, active.id), (r) => `${fmtQty(r.deleted)} row(s) removed.`);
  };

  const editQty = (row) => {
    // eslint-disable-next-line no-alert
    const value = window.prompt(`Quantity for ${row.ean || 'this row'}`, String(row.quantity));
    if (value === null || value.trim() === '' || Number(value) === row.quantity) return;
    run(() => auditService.updateAdjustmentRow(auditId, row.id, { quantity: value.trim() }), 'Quantity updated.');
  };

  const deleteRow = (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete this row (${row.ean || row.remark || 'row'})?`)) return;
    run(() => auditService.deleteAdjustmentRow(auditId, row.id));
  };

  const uploadRows = async (list, replace, fileName) => {
    const result = await auditService.addAdjustmentRows(auditId, active.id, list, { replace });
    setUploading(false);
    setNotice(
      `${replace ? 'Replaced with' : 'Added'} ${fmtQty(result.added)} row(s) from ${fileName}` +
        (result.skipped ? ` · ${fmtQty(result.skipped)} skipped (e.g. ${result.warnings[0]})` : '') +
        '.',
    );
    await Promise.all([loadCategories(), loadRows()]);
  };

  const columns = [
    {
      headerName: 'EAN',
      field: 'ean',
      minWidth: 170,
      getQuickFilterText: (p) => `${p.data.ean || ''} ${p.data.entered_code || ''}`,
      cellRenderer: (p) =>
        p.data.ean ? (
          <span>
            {p.data.ean}
            {p.data.entered_code ? <small className="live-muted"> · entered {p.data.entered_code}</small> : null}
            {!p.data.in_master ? <small className="adjust-flag"> · not in master</small> : null}
          </span>
        ) : (
          '—'
        ),
    },
    { headerName: 'Description', field: 'product_name', minWidth: 200, flex: 2, valueFormatter: (p) => dash(p.value) },
    numberColumn('Qty', 'quantity', fmtQty),
    { headerName: 'Location', field: 'location', valueFormatter: (p) => dash(p.value) },
    { headerName: 'Remark', field: 'remark', minWidth: 200, flex: 2, wrapText: true, autoHeight: true, valueFormatter: (p) => dash(p.value) },
    {
      headerName: 'Added by',
      field: 'created_by_name',
      minWidth: 200,
      getQuickFilterText: (p) => `${p.data.created_by_name || ''} ${when(p.data.created_at)}`,
      cellRenderer: (p) => (
        <>
          {p.data.created_by_name || '—'}
          <small className="live-muted"> {when(p.data.created_at)}</small>
        </>
      ),
    },
    ...(canEdit
      ? [
          actionsColumn(
            (row) => (
              <>
                <button type="button" className="compare-link" onClick={() => editQty(row)}>
                  Edit qty
                </button>
                <button type="button" className="compare-link adjust-delete" onClick={() => deleteRow(row)}>
                  Delete
                </button>
              </>
            ),
            { width: 150 },
          ),
        ]
      : []),
  ];

  if (!categories) {
    return (
      <section className="audit-portal__panel live-panel">
        {error ? <p className="compare-error">{error}</p> : <PageLoader label="Loading manual adjustments…" />}
      </section>
    );
  }

  return (
    <section className="audit-portal__panel live-panel">
      <header className="live-panel__header">
        <div>
          <h2>Manual adjustments</h2>
          <p className="live-muted">
            Stock counted outside the scans. Tabs marked “adds” or “subtracts” change the counted quantity in the
            variance report (Comparison → Scanned vs book stock); remark tabs are notes only.
            {canEdit ? '' : ' Entered by the Admin / Data ASA team.'}
          </p>
        </div>
      </header>

      <div className="adjust-tabs" role="tablist">
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            role="tab"
            aria-selected={category.id === activeId}
            className={`adjust-tab${category.id === activeId ? ' adjust-tab--active' : ''}`}
            onClick={() => {
              setActiveId(category.id);
              setUploading(false);
              setNotice('');
            }}
          >
            <span>{category.name}</span>
            <small className={`adjust-effect adjust-effect--${category.effect}`}>
              {fmtQty(category.qty)} · {EFFECT_SHORT[category.effect]}
            </small>
          </button>
        ))}
        {canEdit ? (
          <button type="button" className="adjust-tab adjust-tab--new" onClick={addRemarksTab}>
            + Remarks tab
          </button>
        ) : null}
      </div>

      {error ? <p className="compare-error">{error}</p> : null}
      {notice ? <p className="live-notice">{notice}</p> : null}

      {active ? (
        <>
          <div className="adjust-bar">
            <div>
              <strong>{active.name}</strong>
              <span className="live-muted">
                {' '}
                · {fmtQty(active.rows)} rows · qty {fmtQty(active.qty)}
                {active.not_in_master ? ` · ${fmtQty(active.not_in_master)} EANs not in the master` : ''}
              </span>
            </div>
            {canEdit ? (
              <label className="adjust-bar__effect">
                Effect
                <select
                  value={active.effect}
                  onChange={(event) =>
                    run(
                      () => auditService.updateAdjustmentCategory(auditId, active.id, { effect: event.target.value }),
                      'Effect saved. The variance report uses it straight away.',
                    )
                  }
                >
                  {Object.entries(EFFECT_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <span className={`adjust-effect adjust-effect--${active.effect}`}>{EFFECT_LABEL[active.effect]}</span>
            )}
          </div>

          {canEdit ? (
            <div className="adjust-actions">
              <button type="button" className="compare-button" onClick={() => setUploading((open) => !open)}>
                {uploading ? 'Close upload' : 'Upload file…'}
              </button>
              <button
                type="button"
                className="compare-button compare-button--ghost"
                onClick={() => downloadTemplate(active)}
                title="CSV with the columns EAN, Qty, Location, Remark and an example row. Delete the example, fill it in, then use Upload file."
              >
                Download template (.csv)
              </button>
              {active.rows ? (
                <button type="button" className="compare-button compare-button--ghost" onClick={clearTab}>
                  Clear tab
                </button>
              ) : null}
              {active.is_custom ? (
                <>
                  <button type="button" className="compare-button compare-button--ghost" onClick={renameTab}>
                    Rename
                  </button>
                  <button type="button" className="compare-button compare-button--ghost adjust-delete" onClick={deleteTab}>
                    Delete tab
                  </button>
                </>
              ) : null}
            </div>
          ) : null}

          {canEdit && uploading ? (
            <AdjustmentUploader
              key={active.id}
              tabName={active.name}
              remarksOnly={active.is_custom}
              onUpload={uploadRows}
              onCancel={() => setUploading(false)}
            />
          ) : null}

          {canEdit ? (
            <AddRowForm
              key={`add-${active.id}`}
              remarksOnly={active.is_custom}
              onAdd={(row) =>
                run(async () => {
                  const result = await auditService.addAdjustmentRows(auditId, active.id, [row]);
                  if (!result.added) throw new Error(result.warnings[0] || 'Row not added.');
                  return result;
                }, 'Row added.')
              }
            />
          ) : null}

          <DataGrid
            key={`rows-${active.id}`}
            rowData={rows}
            columnDefs={columns}
            pageSize={20}
            searchPlaceholder="Search EAN, location, remark or person…"
            emptyMessage={canEdit ? 'No rows yet. Upload a file or add a row above.' : 'No rows in this tab.'}
          />
        </>
      ) : null}
    </section>
  );
}

export default AdjustmentsPanel;
